import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
} from "react-router-dom";
import Root from "layouts/root";
import FlashScreen from "components/page-loader";
import ProtectedRoute from "components/protected";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import * as Sentry from "@sentry/react";
import EmptyState from "components/empty-state";

const ErrorPage = lazy(() => import("pages/error"));
const LoginPage = lazy(() => import("pages/login"));
const RegisterPage = lazy(() => import("pages/register"));
const ForgotPasswordPage = lazy(() => import("pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("pages/reset-password"));
const EmailVerifyPage = lazy(() => import("pages/email-verify"));
const InsightsPage = lazy(() => import("pages/insights/index"));
const TransactionsPage = lazy(() => import("pages/transactions"));
const StatementsListPage = lazy(() => import("pages/statements/index"));
const StatementsDetailsPage = lazy(() => import("pages/statements/details"));
const StatementsUploadPage = lazy(() => import("pages/statements/upload"));
const StatementsConfigEmailPage = lazy(() =>
  import("pages/statements/config-email")
);
const VendorsPage = lazy(() => import("pages/vendors"));
const SettingsIndexPage = lazy(() => import("pages/settings/index"));
const ProfileSettingsPage = lazy(() => import("pages/settings/profile"));

const BankAccountSettingsIndexPage = lazy(() =>
  import("pages/settings/bank-accounts/index")
);
const BankAccountSettingsCreatePage = lazy(() =>
  import("pages/settings/bank-accounts/create")
);
const BankAccountSettingsEditPage = lazy(() =>
  import("pages/settings/bank-accounts/edit")
);

const SelfTransferAccountSettingsIndexPage = lazy(() =>
  import("pages/settings/self-transfer-accounts/index")
);
const SelfTransferAccountSettingsCreatePage = lazy(() =>
  import("pages/settings/self-transfer-accounts/create")
);
const SelfTransferAccountSettingsEditPage = lazy(() =>
  import("pages/settings/self-transfer-accounts/edit")
);

const CategorySettingsIndexPage = lazy(() =>
  import("pages/settings/categories/index")
);
const CategorySettingsDetailsPage = lazy(() =>
  import("pages/settings/categories/details")
);
const ExpenseCategoryCreatePage = lazy(() =>
  import("pages/settings/categories/expense/create")
);
const InvestmentCategoryCreatePage = lazy(() =>
  import("pages/settings/categories/investment/create")
);
const IncomeCategoryCreatePage = lazy(() =>
  import("pages/settings/categories/income/create")
);
const IncomeCategoryEditPage = lazy(() =>
  import("pages/settings/categories/income/edit")
);
const CategorySettingsCreateSubPage = lazy(() =>
  import("pages/settings/categories/create-sub")
);
const CategorySettingsEditPage = lazy(() =>
  import("pages/settings/categories/edit")
);

const MerchantSettingsIndexPage = lazy(() =>
  import("pages/settings/merchants/index")
);

const MerchantSettingsCreatePage = lazy(() =>
  import("pages/settings/merchants/create")
);

const MerchantSettingsEditPage = lazy(() =>
  import("pages/settings/merchants/edit")
);
const SubscriptionSettingsPage = lazy(() =>
  import("pages/settings/subscription")
);
const AboutSettingsPage = lazy(() => import("pages/settings/about"));

const ROUTES_WITHOUT_HEADER_FOOTER = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/email-verify",
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root routesWithoutHeaderFooter={ROUTES_WITHOUT_HEADER_FOOTER} />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <InsightsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/transactions",
        element: (
          <ProtectedRoute>
            <TransactionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/statements",
        element: (
          <ProtectedRoute>
            <StatementsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/statements/upload",
        element: (
          <ProtectedRoute>
            <StatementsUploadPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/statements/config-email",
        element: (
          <ProtectedRoute>
            <StatementsConfigEmailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/statements/:id",
        element: (
          <ProtectedRoute>
            <StatementsDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/vendors",
        element: (
          <ProtectedRoute>
            <VendorsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings",
        element: (
          <ProtectedRoute>
            <SettingsIndexPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/profile",
        element: (
          <ProtectedRoute>
            <ProfileSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/bank-accounts",
        children: [
          {
            path: "/settings/bank-accounts/",
            element: (
              <ProtectedRoute>
                <BankAccountSettingsIndexPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/bank-accounts/create",
            element: (
              <ProtectedRoute>
                <BankAccountSettingsCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/bank-accounts/:id",
            element: (
              <ProtectedRoute>
                <BankAccountSettingsEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/settings/self-transfer-accounts",
        children: [
          {
            path: "/settings/self-transfer-accounts/",
            element: (
              <ProtectedRoute>
                <SelfTransferAccountSettingsIndexPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/self-transfer-accounts/create",
            element: (
              <ProtectedRoute>
                <SelfTransferAccountSettingsCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/self-transfer-accounts/:id",
            element: (
              <ProtectedRoute>
                <SelfTransferAccountSettingsEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/settings/categories",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="expense" replace />,
          },
          {
            path: ":type",
            element: <CategorySettingsIndexPage />,
          },
          {
            path: "expense/create",
            element: <ExpenseCategoryCreatePage />,
          },
          {
            path: "investment/create",
            element: <InvestmentCategoryCreatePage />,
          },
          {
            path: "income/create",
            element: <IncomeCategoryCreatePage />,
          },
          {
            path: "income/:id/edit",
            element: <IncomeCategoryEditPage />,
          },
          {
            path: ":type/:id",
            element: <CategorySettingsDetailsPage />,
          },
          {
            path: ":type/:id/create",
            element: <CategorySettingsCreateSubPage />,
          },
          {
            path: ":type/:id/edit/:subCategoryId",
            element: <CategorySettingsEditPage />,
          },
        ],
      },
      {
        path: "/settings/merchants",
        children: [
          {
            path: "/settings/merchants/",
            element: (
              <ProtectedRoute>
                <MerchantSettingsIndexPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/merchants/create",
            element: (
              <ProtectedRoute>
                <MerchantSettingsCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/settings/merchants/:id",
            element: (
              <ProtectedRoute>
                <MerchantSettingsEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/settings/subscription",
        element: (
          <ProtectedRoute>
            <SubscriptionSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/about",
        element: (
          <ProtectedRoute>
            <AboutSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/login",
        element: <LoginPage hideHeader={true} />,
      },
      {
        path: "/register",
        element: <RegisterPage hideHeader={true} />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPasswordPage hideHeader={true} />,
      },
      {
        path: "/reset-password",
        element: <ResetPasswordPage hideHeader={true} />,
      },
      {
        path: "/email-verify",
        element: <EmailVerifyPage hideHeader={true} />,
      },
    ],
  },
]);

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
  });
}

const root = createRoot(document.getElementById("root"));

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary
      onError={<EmptyState message="Oops, unknown error occurred!" />}
    >
      <Suspense fallback={<FlashScreen />}>
        <RouterProvider router={router} />
      </Suspense>
    </Sentry.ErrorBoundary>
  </StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();
